import {
    fetchCollabToken,
    invalidateCollabTokenCache,
} from '@examdojo/core/tiptap-react-ui-components/lib/collab-token';
import {
    getUrlParam,
    TIPTAP_COLLAB_APP_ID,
    TIPTAP_COLLAB_DOC_PREFIX,
} from '@examdojo/core/tiptap-react-ui-components/lib/tiptap-collab-utils';
import { TiptapCollabProvider } from '@tiptap-pro/provider';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Doc as YDoc } from 'yjs';

export interface CollabContextValue {
  provider: TiptapCollabProvider | null;
  ydoc: YDoc;
  hasCollab: boolean;
  hasError: boolean;
}

export type OptionalCollabContextValue = Pick<CollabContextValue, 'hasCollab' | 'hasError'>;

const DEFAULT_COLLAB_STATUS: OptionalCollabContextValue = Object.freeze({
  hasCollab: false,
  hasError: false,
});

export const CollabContext = createContext<CollabContextValue | null>(null);

export function useCollab(): CollabContextValue {
  const context = useContext(CollabContext);
  if (!context) {
    throw new Error('useCollab must be used within a CollabProvider');
  }
  return context;
}

export function useOptionalCollab(): OptionalCollabContextValue {
  return useContext(CollabContext) ?? DEFAULT_COLLAB_STATUS;
}

const MAX_AUTH_RETRIES = 3;
const AUTH_RETRY_WINDOW_MS = 60_000;

export const useCollaboration = (room: string, getAuthToken: () => Promise<string | null>) => {
  const [provider, setProvider] = useState<TiptapCollabProvider | null>(null);
  const [hasCollab, setHasCollab] = useState<boolean>(true);
  const [hasError, setHasError] = useState(false);
  const ydoc = useMemo(() => new YDoc(), []);

  const authFailures = useRef<number[]>([]);

  const disableCollaboration = useCallback((markAsError: boolean) => {
    setProvider(null);
    setHasCollab(false);
    setHasError(markAsError);
  }, []);

  useEffect(() => {
    const noCollabParam = getUrlParam('noCollab');
    setHasCollab(parseInt(noCollabParam || '0') !== 1);
  }, []);

  const getAuthTokenRef = useRef(getAuthToken);
  useEffect(() => {
    getAuthTokenRef.current = getAuthToken;
  }, [getAuthToken]);

  useEffect(() => {
    if (!hasCollab) return;

    const docPrefix = TIPTAP_COLLAB_DOC_PREFIX;
    const documentName = `${docPrefix}${room}`;
    const appId = TIPTAP_COLLAB_APP_ID;

    setHasError(false);

    let destroyed = false;

    const newProvider = new TiptapCollabProvider({
      name: documentName,
      appId,
      token: () =>
        fetchCollabToken(getAuthTokenRef.current)
          .then((t) => {
            if (!t) {
              if (!destroyed) {
                newProvider.destroy();
                destroyed = true;
              }
              disableCollaboration(false);
              return '';
            }
            return t;
          })
          .catch(() => {
            if (!destroyed) {
              newProvider.destroy();
              destroyed = true;
            }
            disableCollaboration(true);
            return '';
          }),
      document: ydoc,
    });

    newProvider.on('authenticated', () => {
      authFailures.current = [];
    });

    newProvider.on('authenticationFailed', () => {
      if (destroyed) return;

      const now = Date.now();
      authFailures.current = authFailures.current.filter((ts) => now - ts < AUTH_RETRY_WINDOW_MS);
      authFailures.current.push(now);

      if (authFailures.current.length >= MAX_AUTH_RETRIES) {
        disableCollaboration(true);
        return;
      }

      invalidateCollabTokenCache();
    });

    setProvider(newProvider);

    return () => {
      setProvider((currentProvider) => (currentProvider === newProvider ? null : currentProvider));
      if (!destroyed) {
        newProvider.destroy();
        destroyed = true;
      }
    };
  }, [disableCollaboration, ydoc, room, hasCollab]);

  return { provider, ydoc, hasCollab, hasError };
};

export function CollabProvider({
  children,
  room,
  getAuthToken,
}: Readonly<{
  children: React.ReactNode;
  room: string;
  getAuthToken: () => Promise<string | null>;
}>) {
  const { provider, ydoc, hasCollab, hasError } = useCollaboration(room, getAuthToken);

  const value = useMemo<CollabContextValue>(
    () => ({
      provider,
      ydoc,
      hasCollab,
      hasError,
    }),
    [provider, ydoc, hasCollab, hasError],
  );

  return <CollabContext.Provider value={value}>{children}</CollabContext.Provider>;
}
